import { dev } from '$app/environment';
import { Capacitor } from '@capacitor/core';
import { CapacitorUpdater } from '@capgo/capacitor-updater';
import { toast } from '@zerodevx/svelte-toast';

const FAAS_CHECK_VERSION_URL = 'https://faas-sfo3-7872a1dd.doserverless.co/api/v1/web/fn-5d33b76b-2901-497f-821d-db23e80ebc20/default/check-mobile-version';
const FAAS_LIST_TENANTS_URL = 'https://faas-sfo3-7872a1dd.doserverless.co/api/v1/web/fn-5d33b76b-2901-497f-821d-db23e80ebc20/default/list-tenants';

export async function checkIfCurrentVersionIsSupported() {
	if(dev) return true;
	
	const isNativePlatform = Capacitor.isNativePlatform();

	// If we are not running on native platform, we can safely assume that current
	// version is supported as it is running in browser
	if (!isNativePlatform) return true;

	let capacitor;

	// Check if CapacitorUpdater is available ( it is not when running app from local web server )
	try {
		capacitor = await CapacitorUpdater.current();
	} catch (error: any) {
		// If we get UNIMPLEMENTED error, it means we are running app from local web server
		// so we can safely assume that current version is supported
		if (error?.code === 'UNIMPLEMENTED') {
			return true;
		}
	}
	// Otherwise call our serverless function to check if current version is supported
	try {
		// This is version loaded via OTA update. If the device
		// Is running highest appstore version, without any updates,
		// value won't be semantic version but rather string 'builtin'
		const currentBundle = capacitor?.bundle?.version || 'builtin';
		// This is version bundled with a fresh app build
		const currentNativeVersion = capacitor?.native;

		const currentVersion = currentBundle && currentBundle !== 'builtin' ? currentBundle || currentNativeVersion : currentNativeVersion;

		// Call our serverless function to check if current version is supported
		const res = await fetch(FAAS_CHECK_VERSION_URL + '?version=' + currentVersion, {
			headers: {
				'Content-Type': 'application/json'
			}
		});
		const json = await res.json();

		return json.supported;
	} catch (error) {
		toast.push('There was an error checking for updates. If this issue persists, please contact support@campuscommunity.io');
	}
}

// Define the structure of Tenants Response
export type ListTenantsResponse = Array<{
	subdomain: string;
	name: string;
	nameShort: string;
	logo: string;
	logoSm: string;
	iconColor: string;
	env: string;
}>;

// Fetch the tenants from the server
export async function listTenants() {
	try {
		const response = await fetch(FAAS_LIST_TENANTS_URL);
		const json = await response.json();
		return json.data as ListTenantsResponse;
	} catch (error) {
		console.error(error);
		toast.push('Unable to fetch list of institutions. Please close and reopen the app, then try again.');

		return [];
	}
}


export async function listTenantsForSandbox() {
	try {
		const response = await fetch(`${FAAS_LIST_TENANTS_URL}?env=sandbox`);
		const json = await response.json();
		return json.data as ListTenantsResponse;
	} catch (error) {
		console.error(error);
		toast.push('Unable to fetch list of institutions. Please close and reopen the app, then try again.');

		return [];
	}
}

export async function listTenantsForStaging() {
	try {
		const response = await fetch(`${FAAS_LIST_TENANTS_URL}?env=stage`);
		const json = await response.json();
		return json.data as ListTenantsResponse;
	} catch (error) {
		console.error(error);
		toast.push('Unable to fetch list of institutions. Please close and reopen the app, then try again.');

		return [];
	}
}

export async function listTenantsForLocalhost() {
	try {
		const response = await fetch(`${FAAS_LIST_TENANTS_URL}?env=local`);
		const json = await response.json();
		return json.data as ListTenantsResponse;
	} catch (error) {
		console.error(error);
		toast.push('Unable to fetch list of institutions. Please close and reopen the app, then try again.');

		return [];
	}
}
